import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { theme } from '../styles/GlobalStyles';

const ProjectsSection = styled.section`
  padding: ${theme.spacing.xxl} 0;
  background: ${theme.colors.surface};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.md};
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: ${theme.spacing.xxl};
  color: ${theme.colors.text};
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xxl};

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: ${theme.spacing.lg};
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: ${theme.colors.primary};
    transform: translateY(-5px);
  }
`;

const ProjectImage = styled.div<{ bgColor: string }>`
  width: 100%;
  height: 200px;
  background: ${props => props.bgColor};
  border-radius: 12px;
  margin-bottom: ${theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: white;
  font-weight: bold;
`;

const ProjectTitle = styled.h3`
  color: ${theme.colors.text};
  font-size: 1.4rem;
  margin-bottom: ${theme.spacing.sm};
  font-weight: 600;
`;

const ProjectDescription = styled.p`
  color: ${theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.md};
`;

const ProjectTechnologies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.md};
`;

const TechTag = styled.span`
  background: rgba(37, 99, 235, 0.2);
  color: ${theme.colors.primary};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid rgba(37, 99, 235, 0.3);
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const ProjectLink = styled(motion.a)`
  background: ${theme.colors.primary};
  color: white;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.colors.secondary};
    transform: translateY(-2px);
  }
`;

const FeaturedProject = styled(motion.div)`
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.1), rgba(124, 58, 237, 0.1));
  border: 2px solid ${theme.colors.primary};
  border-radius: 20px;
  padding: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xxl};
  text-align: center;
`;

const FeaturedTitle = styled.h3`
  color: ${theme.colors.primary};
  font-size: 1.8rem;
  margin-bottom: ${theme.spacing.lg};
  font-weight: 600;
`;

const FeaturedDescription = styled.p`
  color: ${theme.colors.textSecondary};
  line-height: 1.8;
  font-size: 1.1rem;
  margin-bottom: ${theme.spacing.lg};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const Projects: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const projects = [
    {
      title: 'Dry Tennis',
      description: 'A comprehensive tennis platform connecting players, coaches, and facilities. Features include court booking, lesson scheduling, and community features.',
      technologies: ['React', 'Node.js', 'AWS', 'PostgreSQL', 'Stripe'],
      image: '🎾',
      bgColor: 'linear-gradient(135deg, #10b981, #059669)',
      liveUrl: 'https://dry.tennis',
      githubUrl: '#'
    },
    {
      title: 'E-commerce Platform',
      description: 'Full-stack e-commerce solution with inventory management, payment processing, and analytics dashboard.',
      technologies: ['React', 'Node.js', 'MongoDB', 'Stripe', 'AWS'],
      image: '🛒',
      bgColor: 'linear-gradient(135deg, #f59e0b, #d97706)',
      liveUrl: '#',
      githubUrl: '#'
    },
    {
      title: 'Task Management App',
      description: 'Collaborative task management application with real-time updates, team collaboration, and progress tracking.',
      technologies: ['React', 'TypeScript', 'Firebase', 'Tailwind CSS'],
      image: '✅',
      bgColor: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
      liveUrl: '#',
      githubUrl: '#'
    },
    {
      title: 'Weather Dashboard',
      description: 'Real-time weather application with location-based forecasts, interactive maps, and weather alerts.',
      technologies: ['React', 'OpenWeather API', 'Chart.js', 'CSS Grid'],
      image: '🌤️',
      bgColor: 'linear-gradient(135deg, #06b6d4, #0891b2)',
      liveUrl: '#',
      githubUrl: '#'
    }
  ];

  return (
    <ProjectsSection id="projects" ref={ref}>
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          Featured Projects
        </SectionTitle>

        <FeaturedProject
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <FeaturedTitle>🚀 Currently Building: Dry Tennis</FeaturedTitle>
          <FeaturedDescription>
            I'm currently leading the development of Dry Tennis, an innovative platform that's revolutionizing 
            how tennis players connect, book courts, and improve their game. This project combines my passion 
            for technology with my love for tennis, creating a seamless experience for the tennis community.
          </FeaturedDescription>
          <ProjectLinks>
            <ProjectLink
              href="https://dry.tennis"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Visit Dry Tennis
            </ProjectLink>
            <ProjectLink
              href="#"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Learn More
            </ProjectLink>
          </ProjectLinks>
        </FeaturedProject>

        <ProjectsGrid>
          {projects.map((project, index) => (
            <ProjectCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 + index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <ProjectImage bgColor={project.bgColor}>
                {project.image}
              </ProjectImage>
              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectDescription>{project.description}</ProjectDescription>
              <ProjectTechnologies>
                {project.technologies.map((tech, techIndex) => (
                  <TechTag key={techIndex}>{tech}</TechTag>
                ))}
              </ProjectTechnologies>
              <ProjectLinks>
                {project.liveUrl !== '#' && (
                  <ProjectLink
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Live Demo
                  </ProjectLink>
                )}
                <ProjectLink
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View Code
                </ProjectLink>
              </ProjectLinks>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </Container>
    </ProjectsSection>
  );
};

export default Projects;
